/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useRef, useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import CurrencyIcon from '../../../../components/CurrencyIcon';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { currencies, exchange } from '../../../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { BANK_REQUESTS_COINS } from '../../../Accounts/constants';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { cardProviders } from '../../constants';
import { cryptodraftScreens } from '../constants';
import CryptodraftCurrenciesTabs from '../partials/CurrenciesTabs';
import MainBodyLayout from '../partials/MainBodyLayout';
import { MXP_FC_EXTEND_CONTINUE, MXP_FC_EXTEND_VISIT } from '../../mixpanel-events';
import { FLOWS, sendStats } from '../../../../services/mixpanel';

const CryptodraftCalculator = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const {
    state,
    openScreen,
    updateCollateralAmount,
    updateHintsVisibility,
    updateIsExtendCryptodraft,
  } = useContext(CryptodraftContext);

  const {
    cryptodraft_ltv_ranges: _cryptodraftLtvRanges,
    cryptodraft_interest_mpr: _cryptodraftInterestMpr,
    cryptodraft_us_ltv_ranges: _cryptodraftUSLtvRanges,
    cryptodraft_us_interest_mpr: _cryptodraftUSInterestMpr,
    ...rest
  } = state?.cryptodraftSettings ?? {
    cryptodraft_ltv_ranges: '',
    cryptodraft_interest_mpr: '',
    cryptodraft_us_ltv_ranges: '',
    cryptodraft_us_interest_mpr: '',
  };

  const [minDeposit, setMinDeposit] = useState(0);
  const [maxDeposit, setMaxDeposit] = useState(100);

  const [collateralInputWidth, setCollateralInputWidth] = useState(0);

  const collateralInputSpan = useRef();
  const progressBarRef = useRef();

  const [ltvRangesAndInterest, setLtvRangesAndInterest] = useState([]);

  const isUSUser = state?.user?.isUS;
  const cardProvider = state?.user?.cardProvider;
  const coin = state?.selectedWallet?.coin;
  const availableBalance = state?.selectedWallet?.available_balance;
  const fiatCurrency = state?.fiatCurrency;
  const principalCurrency = state?.principalCurrency;
  const cryptodraftDetails = state?.cryptodraftDetails;
  const screenOptions = state?.screenOptions;
  const cryptodraftTierLTV = state?.cryptodraftTierLTV;

  const rate = useRate(coin, fiatCurrency);
  // just in case user displayCurrency GBP and need to revert to USDT
  const collateralToUSDRate = useRate(coin, BANK_REQUESTS_COINS.USD);

  const fiatBalance = formatNumber(availableBalance * rate, exchange.FIAT_VALUE);

  const [collateralAmount, setCollateralAmount] = useState(0);
  const [collateralAmountDecimal, setCollateralAmountDecimal] = useState(0);

  const collateralAmountInFiatValue =
    fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP &&
    cardProvider === cardProviders.MONAVATE
      ? collateralAmountDecimal * collateralToUSDRate
      : collateralAmountDecimal * rate;

  const title = state.isExtendCryptodraft
    ? translate('cryptodraft_extend_collateral_title')
    : translate('cryptodraft_activate_title');

  const prevScreen = state.isExtendCryptodraft
    ? cryptodraftScreens.DETAILS
    : cryptodraftScreens.PROMO;

  const availableBalanceDisplay = state?.selectedWallet
    ? `${formatNumber(
        availableBalance * currencies[coin?.toUpperCase()].conversion,
        currencies[coin?.toUpperCase()].decimal
      )} ${coin?.toUpperCase()}`
    : 0;

  const cryptoBalanceDecimel = availableBalance * currencies[coin?.toUpperCase()].conversion;
  const validBalance = cryptoBalanceDecimel >= minDeposit;

  useEffect(() => {
    if (rest && state?.selectedWallet) {
      const minDepositRanges =
        rest[`cryptodraft_minimum_initial_deposit_amount_${coin?.toUpperCase()}`];

      const maxDepositRanges =
        rest[`cryptodraft_maximum_initial_deposit_amount_${coin?.toUpperCase()}`];

      setMinDeposit(minDepositRanges);
      setMaxDeposit(maxDepositRanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.selectedWallet, rest, coin]);

  useEffect(() => {
    const charactersLength =
      collateralInputSpan.current.value.length > 1 ? collateralInputSpan.current.value.length : 2;
    setCollateralInputWidth(charactersLength);
  }, [collateralAmount]);

  useEffect(() => {
    if (progressBarRef && progressBarRef?.current) {
      const newWidth = ((collateralAmountDecimal - minDeposit) * 100) / (maxDeposit - minDeposit);
      progressBarRef.current.style.width = `${newWidth > 0 ? newWidth : 0}%`;
    }
  }, [collateralAmountDecimal, progressBarRef, maxDeposit, minDeposit]);

  const parseLTVRanges = (ltvRangesString, interests) => {
    if (ltvRangesString && interests) {
      const interestValues = interests.split(',');
      return ltvRangesString.split(',').map((rangeString, index) => {
        const rangeArray = rangeString.split('-');

        return {
          index,
          start: Number(rangeArray[0]),
          end: rangeArray.length > 1 ? Number(rangeArray[1]) : Number(100),
          interest: interestValues[index],
        };
      });
    }

    return [];
  };

  const cryptodraftLtvRanges = useMemo(
    () => (isUSUser ? _cryptodraftUSLtvRanges : _cryptodraftLtvRanges),
    [_cryptodraftLtvRanges, _cryptodraftUSLtvRanges, isUSUser]
  );

  const cryptodraftInterestMpr = useMemo(
    () => (isUSUser ? _cryptodraftUSInterestMpr : _cryptodraftInterestMpr),
    [_cryptodraftUSInterestMpr, _cryptodraftInterestMpr, isUSUser]
  );

  useEffect(() => {
    setLtvRangesAndInterest(parseLTVRanges(cryptodraftLtvRanges, cryptodraftInterestMpr));
  }, [cryptodraftLtvRanges, cryptodraftInterestMpr]);

  const topLtv = useMemo(() => {
    if (cryptodraftLtvRanges) {
      const ltvRangesValues = cryptodraftLtvRanges?.split(',').map((item) => item.split('-'));
      const ltvRanges = [].concat(...ltvRangesValues);
      return Math.max.apply(null, ltvRanges);
    }

    return 0;
  }, [cryptodraftLtvRanges]);

  const finalLTVRangesAndInterest = useMemo(() => {
    if (!cryptodraftTierLTV || isUSUser) {
      return ltvRangesAndInterest;
    }
    if (cryptodraftTierLTV && topLtv && ltvRangesAndInterest) {
      if (cryptodraftTierLTV < topLtv) {
        const newStartLtvRange = {
          index: 0,
          start: 0,
          end: cryptodraftTierLTV,
          interest: cryptodraftInterestMpr[0],
        };

        const ltvRangesWithoutLtvLessThanTier = ltvRangesAndInterest.filter(
          (item) => item.end > cryptodraftTierLTV
        );

        if (ltvRangesWithoutLtvLessThanTier && ltvRangesWithoutLtvLessThanTier?.length > 0) {
          ltvRangesWithoutLtvLessThanTier[0].start = cryptodraftTierLTV + 0.01;
        }

        return [newStartLtvRange, ...ltvRangesWithoutLtvLessThanTier];
      }

      const newStartLtvRange = {
        index: 0,
        start: 0,
        end: cryptodraftTierLTV,
        interest: cryptodraftInterestMpr[0],
      };
      return [newStartLtvRange];
    }
    return ltvRangesAndInterest;
  }, [cryptodraftTierLTV, ltvRangesAndInterest, topLtv, isUSUser, cryptodraftInterestMpr]);

  const availableCreditLineValue = useMemo(() => {
    if (finalLTVRangesAndInterest) {
      return finalLTVRangesAndInterest.reduce((accumulator, currentValue, index) => {
        if (index === 0) {
          const result = collateralAmountInFiatValue * (currentValue.end / 100);
          return accumulator + result;
        }
        const result =
          collateralAmountInFiatValue *
          ((currentValue.end - finalLTVRangesAndInterest[index - 1].end) / 100);
        return accumulator + result;
      }, 0);
    }
    return 0;
  }, [finalLTVRangesAndInterest, collateralAmountInFiatValue]);

  const handleChangeCollateralAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(formattedAmount, state?.selectedWallet?.wallet_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setCollateralAmount(0);
      setCollateralAmountDecimal(0);
    }

    setCollateralAmount(formattedAmount);
    setCollateralAmountDecimal(decimalAmount);
  };

  const disabledButton = useMemo(
    () =>
      collateralAmountDecimal <= 0 ||
      collateralAmountDecimal > maxDeposit ||
      collateralAmountDecimal < minDeposit ||
      minDeposit === maxDeposit ||
      cryptoBalanceDecimel < collateralAmountDecimal ||
      !validBalance,
    [collateralAmountDecimal, cryptoBalanceDecimel, validBalance, maxDeposit, minDeposit]
  );

  const onPrevClickHandler = () => {
    if (state.isExtendCryptodraft) {
      updateIsExtendCryptodraft();
    }
    if (screenOptions?.openedFromAccountScreen) {
      history.push('/dashboard/card/details/accounts');
    } else {
      openScreen(prevScreen);
    }
  };

  useEffect(() => {
    if (state?.isExtendCryptodraft) {
      sendStats(MXP_FC_EXTEND_VISIT, {}, FLOWS.FC);
    }
  }, []);

  return (
    <MainBodyLayout withBack withInfoIcon onPrevClick={onPrevClickHandler} title={title}>
      <p className="text-shadow text-f6 mt-4 pt-3">
        {translate('cryptodraft_activate_collateral_source_label')}
      </p>
      {state?.isExtendCryptodraft ? (
        <div className="">
          <div className="select-group bg-lighter rounded-extra p-3 d-flex flex-row mt-2 mb-4 select-currency-container">
            <div className="d-flex flex-row align-items-center px-2 card-calculator__select-currency">
              <CurrencyIcon
                currencyCode={coin}
                style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  marginRight: '0.5rem',
                }}
              />
              <p className="m-0 me-2 text-f5">{coin?.toUpperCase()}</p>
            </div>
            <span className="text-right flex-fill">
              <b className="text-f5 m-0">
                {currencies[coin?.toUpperCase()].decimal === exchange.FIAT_VALUE
                  ? formatNumberWithComma(
                      formatNumber(
                        availableBalance * currencies[coin?.toUpperCase()].conversion,
                        currencies[coin?.toUpperCase()].decimal
                      )
                    )
                  : formatNumber(
                      availableBalance * currencies[coin?.toUpperCase()].conversion,
                      currencies[coin?.toUpperCase()].decimal
                    )}{' '}
                {coin?.toUpperCase()}
              </b>
              <p className="text-shadow text-f6 m-0">
                {formatNumberWithComma(
                  formatNumber(
                    fiatBalance * currencies[fiatCurrency?.toUpperCase()].conversion,
                    exchange.FIAT_VALUE
                  )
                )}{' '}
                {fiatCurrency?.toUpperCase()}
              </p>
            </span>
          </div>
        </div>
      ) : (
        <div data-tooltip-id="cryptodraft-hint-2">
          <CryptodraftCurrenciesTabs />
          <p className="text-shadow text-f5">
            {`${translate('available_balance_label')}: `}
            <span className="text-dark">{availableBalanceDisplay}</span>
          </p>
        </div>
      )}

      <p className="text-shadow text-f6 mt-3">{translate('cryptodraft_your_collateral_label')}</p>
      <div className="d-flex flex-row pt-1 my-3 align-items-center justify-content-center">
        <p className="text-shadow text-f6">
          {translate('cryptodraft_available_credit_line_label')}:
        </p>
        <p className="text-f6 text-dark ms-2">{`${formatNumberWithComma(
          formatNumber(availableCreditLineValue, exchange.FIAT_VALUE)
        )} ${fiatCurrency?.toUpperCase()}`}</p>
      </div>

      <FocusableInputArea className="h1 amount-input">
        <input
          required
          max={maxDeposit}
          min={0}
          type="text"
          className="form-control bg-transparent text-bold p-0 text-center border-0"
          value={collateralAmount}
          ref={collateralInputSpan}
          onChange={(e) => handleChangeCollateralAmount(e)}
          placeholder="0 "
          style={{
            fontSize: 'inherit',
            width: `${collateralInputWidth + 1}ch`,
            height: '3rem',
          }}
        />
        <b
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            color: 'inherit',
          }}>
          {coin?.toUpperCase()}
        </b>
      </FocusableInputArea>
      <div className="mb-4 position-relative" data-tooltip-id="cryptodraft-hint-3">
        <div className="slider-progress">
          <div className="progress">
            <div className="progress-bar" ref={progressBarRef} />
          </div>
          <input
            type="range"
            className="form-range"
            value={collateralAmountDecimal}
            max={maxDeposit}
            min={minDeposit}
            step="0.0001"
            onChange={(e) => {
              handleChangeCollateralAmount(e);
            }}
          />
        </div>
        {/* this lines for implementing edges for the slider */}
        <div
          className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
          style={{ top: '15%' }}>
          {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
        </div>
        <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
          <p className="text-f7 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(minDeposit, exchange.FIAT_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
          <p className="text-f7 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(maxDeposit, exchange.FIAT_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
        </div>
      </div>
      {!validBalance && (
        <div
          className="alert alert-warning alert-action mt-4"
          role="button"
          tabIndex={-1}
          onClick={() => {
            openScreen(cryptodraftScreens.ADD_MORE_COLLATERAL);
          }}
          onKeyDown={() => {
            openScreen(cryptodraftScreens.ADD_MORE_COLLATERAL);
          }}>
          <i className="icon icon-info-icon text-shadow" />
          <span className="content">
            {state?.isExtendCryptodraft
              ? translate('more_collateral_extend_cryptodraft_note')
              : translate('more_collateral_active_cryptodraft_note')}
          </span>
          <i className="icon icon-arrow-next-navigation ms-2" />
        </div>
      )}
      {validBalance && (
        <>
          <div className="d-flex mt-4 mt-md-5 flex-column flex-md-row justify-content-center justify-content-md-between align-items-start align-items-md-center gap-3 gap-md-0">
            {finalLTVRangesAndInterest.map((item, index) => (
              <div className="d-flex flex-column" key={item.index}>
                <p className="text-shadow text-f6 text-wrap" style={{ lineHeight: '1rem' }}>
                  <Trans
                    className="mt-5"
                    i18nKey="cryptodraft_ltv_spending_limit_label"
                    values={{
                      range: `${item?.start}-${item?.end}%`,
                      mpr: `${item.interest}%`,
                    }}
                    components={[<span key="mpr-span" className="text-dark" />]}
                  />
                </p>
                <p className="text-dark text-f3 text-bold gap-2 text-nowrap">{`${
                  index === 0
                    ? formatNumberWithComma(
                        formatNumber(
                          collateralAmountInFiatValue * (item.end / 100),
                          exchange.FIAT_VALUE
                        )
                      )
                    : formatNumberWithComma(
                        formatNumber(
                          collateralAmountInFiatValue *
                            ((item.end - finalLTVRangesAndInterest[index - 1].end) / 100),
                          exchange.FIAT_VALUE
                        )
                      )
                } ${principalCurrency?.toUpperCase()}`}</p>
              </div>
            ))}
          </div>
          <p className="text-shadow text-f6 mt-3 pt-1">
            {translate('cryptodraft_activate_note').replace('__CURRENCY__', coin?.toUpperCase())}
          </p>
        </>
      )}
      <div className="btn-container">
        <button
          disabled={disabledButton}
          type="button"
          className="btn py-3 btn-primary w-100"
          onClick={() => {
            if (state.isExtendCryptodraft) {
              sendStats(MXP_FC_EXTEND_CONTINUE, {}, FLOWS.FC);
              const additionalAmount =
                collateralAmountDecimal /
                currencies[cryptodraftDetails?.collateral_currency?.toUpperCase()].conversion;
              updateCollateralAmount(
                parseFloat(
                  formatNumber(
                    additionalAmount *
                      currencies[cryptodraftDetails?.collateral_currency?.toUpperCase()].conversion,
                    exchange.CRYPTO_VALUE
                  )
                )
              );
              openScreen(cryptodraftScreens.REVIEW_EXTEND_OFFER);
            } else {
              updateCollateralAmount(collateralAmountDecimal);
              openScreen(cryptodraftScreens.REVIEW);
            }
            updateHintsVisibility({ first: false, second: false, third: false });
          }}>
          {state.isExtendCryptodraft
            ? translate('extend_cryptodraft_btn_label')
            : translate('preview_cryptodraft_btn_label')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default CryptodraftCalculator;
